import { Injectable } from '@angular/core';
import * as Highcharts from 'highcharts/highstock';
import { cloneDeep, merge } from 'lodash';
import { ReportConfig } from '@app/core/interfaces/report.interface';

@Injectable()
export class AG1GaugeChartService {
  chartOptions: Highcharts.Options = {};

  getOptions(
    reportConfig: ReportConfig,
    reportData: any[],
    defaultGeneralOptions: Highcharts.Options,
  ): Highcharts.Options {
    const defaultGaugeChartOptions = this.getGaugeChartOptions();

    // Merge default, config, and gauge chart options
    const gaugeChartOptions = this.combineChartOptions(
      reportConfig,
      defaultGeneralOptions,
      defaultGaugeChartOptions,
    );

    // Prepare and set series options
    gaugeChartOptions.series = this.prepareSeriesOptions(
      reportConfig,
      reportData,
      gaugeChartOptions.series[0] as Highcharts.SeriesGaugeOptions,
    );

    // Prepare and set yAxis options
    gaugeChartOptions.yAxis = this.prepareYAxisOptions(
      reportConfig,
      gaugeChartOptions,
    );

    this.chartOptions = gaugeChartOptions;
    return this.chartOptions;
  }

  private getGaugeChartOptions(): Highcharts.Options {
    return {
      chart: this.getGaugeChartType(),
      legend: this.getLegendOptions(),
      tooltip: this.getTooltipOptions(),
      series: this.getSeriesOptions(),
      yAxis: this.getYAxisOptions(),
      pane: this.getPaneOptions(),
    };
  }
  /////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////
  // #region         getOptions helpers           //
  //////////////////////////////////////////////////
  private combineChartOptions(
    reportConfig: ReportConfig,
    defaultGeneralOptions: Highcharts.Options,
    defaultGaugeChartOptions: Highcharts.Options,
  ): Highcharts.Options {
    const configChartOptions = cloneDeep(reportConfig?.chartOptions);

    // Merge default, pie chart, and config-specific options
    const pieChartOptions = merge(
      {},
      defaultGeneralOptions,
      defaultGaugeChartOptions,
      configChartOptions,
    );

    return pieChartOptions;
  }

  private prepareSeriesOptions(
    reportConfig: ReportConfig,
    reportData: any[],
    gaugeSeriesOptions: Highcharts.SeriesGaugeOptions,
  ): Highcharts.SeriesGaugeOptions[] {
    const seriesOptions: Highcharts.SeriesGaugeOptions[] = [];
    const suffix = reportConfig?.highChartsCustom?.suffix;

    // Add suffix to dataLabels if present
    if (suffix) {
      gaugeSeriesOptions.dataLabels = {
        ...gaugeSeriesOptions.dataLabels,
        format: `{y}${suffix}`,
      };
    }

    // Add series data if available
    if (reportData.length > 0) {
      const key = Object.keys(reportData[0])[0];
      const value = reportData[0][key];
      seriesOptions.push({
        ...gaugeSeriesOptions,
        data: [value],
        name: key,
      });
    }

    return seriesOptions;
  }

  private prepareYAxisOptions(
    reportConfig: ReportConfig,
    gaugeChartOptions: Highcharts.Options,
  ): Highcharts.YAxisOptions {
    const plotBands = reportConfig.chartOptions.yAxis['plotBands'];

    // Extract unique tick positions from plotBands
    const tickPositions = this.getUniqueTickPositions(plotBands);

    return merge(
      {},
      gaugeChartOptions.yAxis,
      { tickPositions },
      {
        plotBands: plotBands.map((band) => ({ ...band, thickness: '20%' })),
      },
    );
  }

  private getUniqueTickPositions(plotBands): number[] {
    const uniqueValues = new Set<number>(
      plotBands.flatMap((band) => [band.from, band.to]),
    );
    return Array.from(uniqueValues).sort((a, b) => a - b);
  }
  /////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////
  // #region    getGaugeChartOptions helpers      //
  //////////////////////////////////////////////////
  private getGaugeChartType(): Highcharts.ChartOptions {
    return { type: 'gauge' };
  }

  private getLegendOptions(): Highcharts.LegendOptions {
    return { enabled: false };
  }

  private getTooltipOptions(): Highcharts.TooltipOptions {
    return { enabled: false };
  }

  private getSeriesOptions(): Highcharts.SeriesGaugeOptions[] {
    return [
      {
        type: 'gauge',
        dataLabels: {
          color: '#334155',
          y: -35,
          borderWidth: 0,
          style: {
            fontSize: '200%',
            fontWeight: '500',
          },
        },
        dial: {
          radius: '90%',
          backgroundColor: '#334155',
          borderColor: 'white',
          borderWidth: 2,
          baseWidth: 20,
          baseLength: '80%',
          rearLength: '-80%',
        },
        pivot: {
          backgroundColor: 'none',
        },
      } as Highcharts.SeriesGaugeOptions,
    ];
  }

  private getYAxisOptions(): Highcharts.YAxisOptions {
    return {
      tickColor: '#FFFFFF',
      tickLength: 30,
      tickWidth: 5,
      minorTickInterval: null,
      labels: {
        distance: 20,
        style: {
          fontSize: '16px',
        },
      },
      lineWidth: 0,
    };
  }

  private getPaneOptions(): Highcharts.PaneOptions {
    return {
      background: null,
      startAngle: -120,
      endAngle: 120,
    };
  }
}
